/**
 * Announcements (TMMPDM-0110; TMMAPI-0181 item 32a): the words and small rules the list and the
 * editor share. A state and a read count are shown as the server sent them; nothing here derives a
 * state from the times. The leak guard and the audience are the server's to enforce.
 */
import { ApiError } from '@/api/errors';
import {
  ANNOUNCEMENT_KINDS,
  type AnnouncementKind,
  type AnnouncementRow,
  type AnnouncementState,
} from '@/api/tmm/types';
import type { Translate } from './tickets';

export const TITLE_MAX = 120;
export const BODY_MAX = 8000;

const KIND_FALLBACK: Record<AnnouncementKind, string> = {
  NEWS: 'News',
  FEATURE: 'New feature',
  MAINTENANCE: 'Maintenance',
  IMPORTANT: 'Important',
};

const STATE_FALLBACK: Record<AnnouncementState, string> = {
  DRAFT: 'Draft',
  SCHEDULED: 'Scheduled',
  PUBLISHED: 'Published',
  EXPIRED: 'Expired',
};

/** A kind's label; one this console doesn't know shows its code. */
export function kindLabel(kind: string, t: Translate): string {
  const fallback = (ANNOUNCEMENT_KINDS as readonly string[]).includes(kind)
    ? KIND_FALLBACK[kind as AnnouncementKind]
    : kind;
  return t(`announcements.kind.${kind}`, fallback);
}

/** A state's label, as the server sent the state; one this console doesn't know shows its code. */
export function stateLabel(state: string, t: Translate): string {
  const fallback = Object.hasOwn(STATE_FALLBACK, state)
    ? STATE_FALLBACK[state as keyof typeof STATE_FALLBACK]
    : state;
  return t(`announcements.state.${state}`, fallback);
}

/** "All customers", or "{{n}} tenants" from the list the server sent. */
export function audienceLabel(
  row: Pick<AnnouncementRow, 'audience' | 'tenantIds'>,
  t: Translate,
): string {
  if (row.audience === 'ALL') return t('announcements.audienceAll', 'All customers');
  const count = row.tenantIds?.length ?? 0;
  return t('announcements.audienceTenants', '{{count, number}} tenants', { count });
}

/**
 * A 404 that names nothing it couldn't find: tmm-api hasn't deployed the announcement routes yet.
 * `ANNOUNCEMENT_NOT_FOUND` is a deployed route's answer for a missing one.
 */
export function isAnnouncementsNotDeployed(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    error.status === 404 &&
    String(error.code) !== 'ANNOUNCEMENT_NOT_FOUND'
  );
}

export function isAnnouncementNotFound(error: unknown): boolean {
  return error instanceof ApiError && String(error.code) === 'ANNOUNCEMENT_NOT_FOUND';
}

/**
 * 409 `ANNOUNCEMENT_NOT_DRAFT` (TMMPDM-0111; 0181 U5 YAML): an edit, publish or delete once it's no
 * longer a draft (someone published or scheduled it meanwhile). The editor re-reads it.
 */
export function isAnnouncementNotDraft(error: unknown): boolean {
  return error instanceof ApiError && String(error.code) === 'ANNOUNCEMENT_NOT_DRAFT';
}

/** The `/tickets/announcements/:id` param: "new", or a positive whole number. Anything else is null. */
export function parseAnnouncementId(raw: string): number | 'new' | null {
  if (raw === 'new') return 'new';
  if (!/^\d{1,15}$/.test(raw)) return null;
  const value = Number(raw);
  return Number.isSafeInteger(value) && value > 0 ? value : null;
}

export const ANNOUNCEMENTS_TAB = '/tickets?tab=announcements';

export function announcementPath(id: number | 'new'): string {
  return `/tickets/announcements/${id}`;
}

/**
 * A `datetime-local` value ("2026-10-20T09:30", the person's own time) as an instant; null when it
 * isn't a whole, real local time. Built from its parts, never by parsing a string.
 */
export function localInputToInstant(value: string): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(value);
  if (!match) return null;
  const [year, month, day, hour, minute] = match.slice(1).map(Number) as [
    number,
    number,
    number,
    number,
    number,
  ];
  const date = new Date(year, month - 1, day, hour, minute, 0, 0);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day ||
    date.getHours() !== hour ||
    date.getMinutes() !== minute
  ) {
    return null;
  }
  return date.toISOString();
}

/** An instant as a `datetime-local` value in the person's own time ("" when absent). */
export function instantToLocalInput(instant: string | undefined): string {
  if (instant === undefined) return '';
  const date = new Date(Date.parse(instant));
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours(),
  )}:${pad(date.getMinutes())}`;
}
